import {
    ACTIVE_SEASONAL_EVENTBRITE_CATEGORY,
    DATING_EVENTBRITE_SUB_CATEGORY,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
} from '@eventbrite/categories';
import { History, Location } from 'history';
import React from 'react';
import { match } from 'react-router-dom';
import { OrganicEventSearchFiltersApiPayload } from '../../api/searchOrganicEvents';
import { DateType } from '../../constants/dates';
import { FeatureFlags } from '../../types';
import { DATING_TAGS } from './components/CategoryFilterBar/constants';
import { DATING, DatingTabsSearchQuery } from './constants/dating';
import {
    DEFAULT_NIGHTLIFE_TAB,
    NightlifeTabsSearchQuery,
} from './constants/nightLife';
import { SeasonalTabsSearchQuery } from './constants/seasonal';
import { LocationProvider, RouteProvider, SearchProvider } from './context';
import { useLocationByLocationSlug } from './hooks/queries/location';
import { parseEventSearchFromUrlParams } from './utils';
export interface CategoryBrowseRouteParams {
    locationSlug: string;
    category: string;
    subcategory: string;
}
export type CategoryBrowseRouteMatch = match<CategoryBrowseRouteParams>;
export interface CategoryBrowseProviderProps {
    match: match<CategoryBrowseRouteParams>;
    location: Location;
    history: History;
    location_slug: string;
    location_data: {
        displayLocation: string;
        region: string;
        latitude: number;
        longitude: number;
        isOnline: boolean;
        placeId: string;
        placeType: string;
        country: string;
        countrySlug?: string;
        city?: string;
        citySlug?: string;
        borough?: string;
        boroughSlug?: string;
    };
    featureFlags?: FeatureFlags;
}

const getSearchParams = (
    location: Location,
    match: CategoryBrowseRouteMatch,
) => {
    const params = new URLSearchParams(location.search);
    const { activeTag, date, customDate, category, bucket } =
        parseEventSearchFromUrlParams(params, match);
    return {
        activeTag,
        date,
        customDate,
        category,
        bucket,
    };
};

const getDateArray = (
    category: string,
    dateArray: DateType[],
    TabsSearchQuery: any,
    defaultTab: string,
) => {
    const TagDateArray = category
        ? TabsSearchQuery[category]?.dates
            ? TabsSearchQuery[category].dates
            : dateArray
        : TabsSearchQuery[defaultTab]?.dates
        ? TabsSearchQuery[defaultTab].dates
        : dateArray;
    return TagDateArray ? TagDateArray : dateArray;
};

export const CategoryBrowseProviders: React.FunctionComponent<
    CategoryBrowseProviderProps
> = (props) => {
    const { match, location, history, location_data, featureFlags } = props;
    const { activeTag, date, customDate, category } = getSearchParams(
        location,
        match,
    );

    let { bucket } = getSearchParams(location, match);

    if (activeTag === ACTIVE_SEASONAL_EVENTBRITE_CATEGORY) {
        bucket = bucket || 'BarCrawl';
    }

    let currentPlaceParent;
    switch (location_data?.placeType) {
        case 'region':
            currentPlaceParent = location_data?.country;
            break;
        case 'neighbourhood':
        case 'borough':
            currentPlaceParent = location_data?.city;
            break;
        default:
            currentPlaceParent = location_data?.region;
    }

    const initialLocation = location_data
        ? {
              currentPlace:
                  location_data?.displayLocation || location_data?.city,
              currentPlaceParent: currentPlaceParent,
              slug: match.params.locationSlug,
              latitude: location_data?.latitude,
              longitude: location_data?.longitude,
              isOnline: location_data?.isOnline,
              placeId: location_data?.placeId,
              placeType: location_data?.placeType,
              country: location_data?.country,
              countrySlug: location_data?.countrySlug,
              city: location_data?.city,
              citySlug: location_data?.citySlug,
              borough: location_data?.borough,
              boroughSlug: location_data?.boroughSlug,
          }
        : {};
    const isNightlifeDestination = activeTag === NIGHTLIFE_EVENTBRITE_CATEGORY;
    const dateArray: DateType[] =
        date === 'current_future'
            ? ['current_future']
            : ['current_future', date];

    const initialQuery: OrganicEventSearchFiltersApiPayload = {
        tags: (() => {
            switch (activeTag) {
                case NIGHTLIFE_EVENTBRITE_CATEGORY:
                    return category
                        ? NightlifeTabsSearchQuery[category].tags
                        : NightlifeTabsSearchQuery[DEFAULT_NIGHTLIFE_TAB].tags;
                case ACTIVE_SEASONAL_EVENTBRITE_CATEGORY:
                    return bucket
                        ? SeasonalTabsSearchQuery[bucket].tags
                        : undefined;
                case DATING_EVENTBRITE_SUB_CATEGORY:
                    return category
                        ? DatingTabsSearchQuery[category]?.tags
                        : DATING_TAGS;
                default:
                    return [activeTag || ''];
            }
        })(),
        q: (() => {
            switch (activeTag) {
                case ACTIVE_SEASONAL_EVENTBRITE_CATEGORY:
                    return bucket
                        ? SeasonalTabsSearchQuery[bucket].q
                        : undefined;
                case DATING_EVENTBRITE_SUB_CATEGORY:
                    return category
                        ? DatingTabsSearchQuery[category]?.q
                        : DATING;
                default:
                    return undefined;
            }
        })(),
        start_hour_range: isNightlifeDestination
            ? {
                  start_hour: 20,
                  end_hour: 24,
              }
            : undefined,
        dates: (() => {
            switch (activeTag) {
                case NIGHTLIFE_EVENTBRITE_CATEGORY:
                    return getDateArray(
                        category,
                        dateArray,
                        NightlifeTabsSearchQuery,
                        DEFAULT_NIGHTLIFE_TAB,
                    );
                case DATING_EVENTBRITE_SUB_CATEGORY:
                    return getDateArray(
                        category,
                        dateArray,
                        DatingTabsSearchQuery,
                        'All',
                    );
                default:
                    return dateArray;
            }
        })(),
        date_range: customDate,
    };

    //Initialize location if hydrated via props
    useLocationByLocationSlug(match.params.locationSlug, initialLocation);

    return (
        <RouteProvider
            match={match}
            location={location}
            history={history}
            parsedValues={{
                activeTag,
                date,
                customDate,
                category:
                    category !== DEFAULT_NIGHTLIFE_TAB && category !== null
                        ? (category as string)
                        : undefined,
                bucket: bucket as string,
            }}
            featureFlags={featureFlags}
        >
            <LocationProvider locationSlug={match.params.locationSlug}>
                <SearchProvider initialQuery={initialQuery}>
                    {props.children}
                </SearchProvider>
            </LocationProvider>
        </RouteProvider>
    );
};
